import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import './Settings.css';

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settingsId, setSettingsId] = useState(null);
    const [activeTab, setActiveTab] = useState('identity');

    const [formData, setFormData] = useState({
        // 1: Identité
        company_name: "Coup d'Projo",
        slogan: "Parce que chaque marque mérite la lumière.",
        description: "Agence créative spécialisée dans la production audiovisuelle et la stratégie digitale pour marques audacieuses.",
        logo_light: null,
        logo_dark: null,

        // 2: Contact
        email_public: 'contact@coupdprojo.fr',
        phone_public: '06 00 00 00 00',
        address_office: 'BP 123, 81300 Graulhet, Tarn',
        maps_link: '',
        socials: { 
            instagram: 'https://instagram.com/coupdprojo', 
            facebook: 'https://facebook.com/coupdprojo', 
            linkedin: 'https://linkedin.com/company/coupdprojo', 
            tiktok: 'https://tiktok.com/@coupdprojo' 
        },

        // 3: Facturation
        billing_raison_sociale: "COUP D'PROJO SAS",
        billing_siret: '10110746400014',
        billing_siren: '101107464',
        billing_tva: 'FR09101107464',
        billing_rcs: '101 107 464 R.C.S. Paris',
        billing_tribunal: 'PARIS',
        billing_contact_phone: '07 69 29 79 63',
        billing_contact_email: 'contact@infocolab.com',
        billing_contact_website: 'https://www.infocolab.com/',
        billing_address: '60 RUE FRANCOIS IER',
        billing_zip: '75008',
        billing_city: 'PARIS',
        num_devis_prefix: 'DEV',
        num_devis_counter: 69,
        num_facture_prefix: 'FACT',
        num_facture_counter: 1,
        bank_holder: 'INFOCOLAB',
        bank_name: 'Banque Projo / Infocolab',
        bank_address: 'Paris, 75008',
        bank_currency: '€',
        bank_iban: 'FR76 1000 0000 0000 0000 0000 000',
        bank_bic: 'ABCDEFGH',
        footer_devis: 'Devis valable 30 jours à compter de la date d\'émission.',
        footer_facture: 'TVA non applicable, art. 293 B du CGI',
        cond_reglement_text: '30 jours à réception',
        cond_penalties_pct: 10,
        cond_acompte_pct: 30,

        // 4: Horaires
        opening_hours: {
            monday: { open: true, start: '09:00', end: '18:00' },
            tuesday: { open: true, start: '09:00', end: '18:00' },
            wednesday: { open: true, start: '09:00', end: '18:00' },
            thursday: { open: true, start: '09:00', end: '18:00' },
            friday: { open: true, start: '09:00', end: '17:00' },
            saturday: { open: false, start: '', end: '' },
            sunday: { open: false, start: '', end: '' },
        },
        response_time_est: 'Sous 2 heures',

        // 5: Fermetures
        closure_banner_active: false,
        closure_auto_responder: "Bonjour, je suis en déplacement extérieur. Je reviens vers vous dès mon retour à l'agence. Merci de votre confiance !",
        busy_mode_active: false,

        // 6: Profil / Légalm
        profile_firstName: 'Admin',
        profile_lastName: 'Coupdprojo'
    });

    const [previews, setPreviews] = useState({ light: null, dark: null });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            // 1. Load Company Settings
            const records = await pb.collection('company_settings').getList(1, 1);
            if (records.items.length > 0) {
                const r = records.items[0];
                setSettingsId(r.id);
                setFormData(prev => ({ 
                    ...prev, 
                    ...r,
                    socials: typeof r.socials === 'string' ? JSON.parse(r.socials) : (r.socials || prev.socials),
                    opening_hours: typeof r.opening_hours === 'string' ? JSON.parse(r.opening_hours) : (r.opening_hours || prev.opening_hours)
                }));
                setPreviews({
                    light: r.logo_light ? pb.files.getUrl(r, r.logo_light) : null,
                    dark: r.logo_dark ? pb.files.getUrl(r, r.logo_dark) : null
                });
            }

            // 2. Load Profile from current user
            if (pb.authStore.model) {
                const user = await pb.collection('users').getOne(pb.authStore.model.id);
                setFormData(prev => ({
                    ...prev,
                    profile_firstName: user.firstName || '',
                    profile_lastName: user.lastName || '',
                }));
            }
        } catch (err) {
            console.log("Error loading settings:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: val }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleHourChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            opening_hours: {
                ...prev.opening_hours,
                [day]: { ...prev.opening_hours[day], [field]: value }
            }
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, [`logo_${type}_file`]: file }));
            setPreviews(prev => ({ ...prev, [type]: URL.createObjectURL(file) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // 1. Save Company Settings
            const companyData = new FormData();
            
            // Explicitly list fields that go to company_settings
            const companyFields = [
                'company_name', 'slogan', 'description', 'email_public', 'phone_public', 
                'address_office', 'maps_link', 'socials', 'opening_hours',
                'billing_raison_sociale', 'billing_siren', 'billing_rcs', 'billing_tribunal', 
                'billing_contact_phone', 'billing_contact_email', 'billing_address', 'billing_zip', 'billing_city',
                'num_devis_prefix', 'num_devis_counter', 'num_facture_prefix', 'num_facture_counter',
                'bank_holder', 'bank_name', 'bank_address', 'bank_currency', 'bank_iban', 'bank_bic', 'footer_devis', 'footer_facture',
                'cond_reglement_text', 'cond_penalties_pct', 'cond_acompte_pct',
                'busy_mode_active', 'closure_banner_active', 'closure_auto_responder'
            ];

            // Map UI fields to PB fields
            companyFields.forEach(key => {
                let value = formData[key];
                if (key === 'socials' || key === 'opening_hours') {
                    companyData.append(key, JSON.stringify(value));
                } else {
                    companyData.append(key, value);
                }
            });

            if (formData.logo_light_file) companyData.append('logo_light', formData.logo_light_file);
            if (formData.logo_dark_file) companyData.append('logo_dark', formData.logo_dark_file);

            if (settingsId) {
                await pb.collection('company_settings').update(settingsId, companyData);
            } else {
                const res = await pb.collection('company_settings').create(companyData);
                setSettingsId(res.id);
            }

            // 2. Save Profile (Users collection)
            if (pb.authStore.model) {
                await pb.collection('users').update(pb.authStore.model.id, {
                    firstName: formData.profile_firstName,
                    lastName: formData.profile_lastName
                });
            }

            alert('🚀 Configuration synchronisée avec succès !');
        } catch (err) {
            console.error(err);
            alert('Erreur technique : ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-content-wrapper"><p>Chargement...</p></div>;

    const navItems = [
        { id: 'identity', label: '1. Identité', icon: '🎨' },
        { id: 'contact', label: '2. Contact', icon: '📱' },
        { id: 'billing', label: '3. Facturation', icon: '💶' },
        { id: 'hours', label: '4. Horaires', icon: '📅' },
        { id: 'closures', label: '5. Fermetures', icon: '🔒' },
        { id: 'legal', label: '6. Profil / Légal', icon: '⚖️' },
    ];

    return (
        <div className="admin-content-wrapper settings-overhaul">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Configuration Globale</h2>
                    <p>Interface de pilotage de l'agence.</p>
                </div>
            </section>

            <div className="settings-container-split">
                <aside className="settings-nav-aside">
                    {navItems.map(item => (
                        <button 
                            key={item.id} 
                            className={`settings-nav-btn ${activeTab === item.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(item.id)}
                        >
                            <span className="nav-icon">{item.icon}</span>
                            <span className="nav-label">{item.label}</span>
                        </button>
                    ))}
                </aside>

                <form onSubmit={handleSubmit} className="settings-form-main">
                    <div className="active-tab-content fade-in">
                        
                        {activeTab === 'identity' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Identité de Marque</h3>
                                <div className="logo-upload-grid">
                                    <div className="logo-upload-card light-bg">
                                        <label>Logo Version Claire</label>
                                        <div className="logo-preview-box dark-preview">
                                            {previews.light ? <img src={previews.light} alt="Light" /> : <span>Logo</span>}
                                        </div>
                                        <input type="file" onChange={(e) => handleFileChange(e, 'light')} />
                                    </div>
                                    <div className="logo-upload-card dark-bg">
                                        <label>Logo Version Sombre</label>
                                        <div className="logo-preview-box light-preview">
                                            {previews.dark ? <img src={previews.dark} alt="Dark" /> : <span>Logo</span>}
                                        </div>
                                        <input type="file" onChange={(e) => handleFileChange(e, 'dark')} />
                                    </div>
                                </div>
                                <div className="form-group-stack">
                                    <label>Nom de l'Agence</label>
                                    <input type="text" name="company_name" value={formData.company_name} onChange={handleChange} className="admin-input-neo" />
                                    <label>Slogan Principal</label>
                                    <input type="text" name="slogan" value={formData.slogan} onChange={handleChange} className="admin-input-neo" />
                                    <label>Description Courte</label>
                                    <textarea name="description" value={formData.description} onChange={handleChange} className="admin-input-neo" rows="4"></textarea>
                                </div>
                            </div>
                        )}

                        {activeTab === 'contact' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Contact & Localisation</h3>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Email Public</label><input type="email" name="email_public" value={formData.email_public} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Téléphone</label><input type="text" name="phone_public" value={formData.phone_public} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <div className="form-group-stack">
                                    <label>Adresse Siège Social</label>
                                    <input type="text" name="address_office" value={formData.address_office} onChange={handleChange} className="admin-input-neo" />
                                    <label>Lien Google Maps</label>
                                    <input type="text" name="maps_link" value={formData.maps_link} onChange={handleChange} className="admin-input-neo" />
                                </div>
                                <div className="section-header-flex">
                                    <h4 className="sub-section-title">Écosystème Digital</h4>
                                    <button 
                                        type="button" 
                                        className="add-network-btn-mini"
                                        onClick={() => {
                                            const name = prompt("Nom du réseau (ex: Twitter, Youtube...)");
                                            if (name) setFormData(prev => ({ ...prev, socials: { ...prev.socials, [name.toLowerCase()]: '' } }));
                                        }}
                                    >
                                        + Ajouter un réseau
                                    </button>
                                </div>
                                <div className="social-links-manager-grid">
                                    {Object.entries(formData.socials).map(([platform, url]) => (
                                        <div key={platform} className="social-input-wrapper">
                                            <div className="social-platform-meta">
                                                <div className={`social-icon-box ${platform}`}>
                                                    {platform === 'instagram' && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>}
                                                    {platform === 'facebook' && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>}
                                                    {platform === 'linkedin' && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>}
                                                    {platform === 'tiktok' && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path></svg>}
                                                    {!['instagram', 'facebook', 'linkedin', 'tiktok'].includes(platform) && <span style={{fontSize: '10px'}}>{platform.slice(0,2).toUpperCase()}</span>}
                                                </div>
                                                <label className="capitalize">{platform}</label>
                                            </div>
                                            <div className="social-input-actions">
                                                <input 
                                                    type="text" 
                                                    value={url} 
                                                    onChange={(e) => setFormData(prev => ({ 
                                                        ...prev, 
                                                        socials: { ...prev.socials, [platform]: e.target.value } 
                                                    }))} 
                                                    className="admin-input-neo" 
                                                    placeholder="URL du profil"
                                                />
                                                <button 
                                                    type="button" 
                                                    className="remove-social-btn" 
                                                    onClick={() => {
                                                        const newSocials = { ...formData.socials };
                                                        delete newSocials[platform];
                                                        setFormData(prev => ({ ...prev, socials: newSocials }));
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'billing' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Identité Fiscale & Légale</h3>
                                <div className="form-group-stack"><label>Raison Sociale</label><input type="text" name="billing_raison_sociale" value={formData.billing_raison_sociale} onChange={handleChange} className="admin-input-neo" /></div>
                                <div className="grid-3-settings">
                                    <div className="form-group-stack"><label>SIRET</label><input type="text" name="billing_siret" value={formData.billing_siret} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>SIREN</label><input type="text" name="billing_siren" value={formData.billing_siren} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Numéro TVA</label><input type="text" name="billing_tva" value={formData.billing_tva} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>RCS</label><input type="text" name="billing_rcs" value={formData.billing_rcs} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Tribunal de Commerce</label><input type="text" name="billing_tribunal" value={formData.billing_tribunal} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <h4 className="sub-section-title">Contact Facturation</h4>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Téléphone</label><input type="text" name="billing_contact_phone" value={formData.billing_contact_phone} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Email</label><input type="email" name="billing_contact_email" value={formData.billing_contact_email} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <div className="form-group-stack">
                                    <label>Adresse Facturation</label>
                                    <input type="text" name="billing_address" value={formData.billing_address} onChange={handleChange} className="admin-input-neo" />
                                </div>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Code Postal</label><input type="text" name="billing_zip" value={formData.billing_zip} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Ville</label><input type="text" name="billing_city" value={formData.billing_city} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <h4 className="sub-section-title">Numérotation Automatique</h4>
                                <div className="numbering-stack-settings">
                                    <div className="numbering-box">
                                        <h5>📂 Devis</h5>
                                        <div className="grid-2-settings">
                                            <div className="form-group-stack"><label>Préfixe</label><input placeholder="Préfixe" name="num_devis_prefix" value={formData.num_devis_prefix} onChange={handleChange} className="admin-input-neo" /></div>
                                            <div className="form-group-stack"><label>Compteur</label><input type="number" name="num_devis_counter" value={formData.num_devis_counter} onChange={handleChange} className="admin-input-neo" /></div>
                                        </div>
                                    </div>
                                    <div className="numbering-box highlight">
                                        <h5>💵 Factures</h5>
                                        <div className="grid-2-settings">
                                            <div className="form-group-stack"><label>Préfixe</label><input placeholder="Préfixe" name="num_facture_prefix" value={formData.num_facture_prefix} onChange={handleChange} className="admin-input-neo" /></div>
                                            <div className="form-group-stack"><label>Compteur</label><input type="number" name="num_facture_counter" value={formData.num_facture_counter} onChange={handleChange} className="admin-input-neo" /></div>
                                        </div>
                                    </div>
                                </div>
                                <h4 className="sub-section-title">Détails Bancaires</h4>
                                <div className="form-group-stack"><label>Titulaire du Compte</label><input type="text" name="bank_holder" value={formData.bank_holder} onChange={handleChange} className="admin-input-neo" /></div>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Nom de la Banque</label><input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Adresse Banque</label><input type="text" name="bank_address" value={formData.bank_address} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Devise</label><input type="text" name="bank_currency" value={formData.bank_currency} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>BIC / SWIFT</label><input type="text" name="bank_bic" value={formData.bank_bic} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                                <div className="form-group-stack">
                                    <label>IBAN</label>
                                    <input type="text" name="bank_iban" value={formData.bank_iban} onChange={handleChange} className="admin-input-neo font-mono" />
                                </div>

                                <h4 className="sub-section-title">Pieds de Page (Clauses)</h4>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack"><label>Footer Devis</label><textarea name="footer_devis" value={formData.footer_devis} onChange={handleChange} className="admin-input-neo" rows="2"></textarea></div>
                                    <div className="form-group-stack"><label>Footer Facture</label><textarea name="footer_facture" value={formData.footer_facture} onChange={handleChange} className="admin-input-neo" rows="2"></textarea></div>
                                </div>

                                <h4 className="sub-section-title">Conditions Financières</h4>
                                <div className="grid-3-settings">
                                    <div className="form-group-stack"><label>Délai Règlement</label><input type="text" name="cond_reglement_text" value={formData.cond_reglement_text} onChange={handleChange} className="admin-input-neo" placeholder="ex: 30 jours à réception" /></div>
                                    <div className="form-group-stack"><label>Taux Pénalités (%)</label><input type="number" name="cond_penalties_pct" value={formData.cond_penalties_pct} onChange={handleChange} className="admin-input-neo" /></div>
                                    <div className="form-group-stack"><label>Acompte Défaut (%)</label><input type="number" name="cond_acompte_pct" value={formData.cond_acompte_pct} onChange={handleChange} className="admin-input-neo" /></div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'hours' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Horaires & Disponibilité</h3>
                                <div className="hours-config-list">
                                    {Object.keys(formData.opening_hours).map(day => (
                                        <div key={day} className="day-hour-row">
                                            <div className="day-info">
                                                <span className="day-name">{day}</span>
                                                <label className="switch-neo"><input type="checkbox" checked={formData.opening_hours[day].open} onChange={(e) => handleHourChange(day, 'open', e.target.checked)} /><span className="slider-neo"></span></label>
                                            </div>
                                            {formData.opening_hours[day].open && (
                                                <div className="hour-inputs"><input type="time" value={formData.opening_hours[day].start} onChange={(e) => handleHourChange(day, 'start', e.target.value)} className="admin-input-neo-time" /><span>à</span><input type="time" value={formData.opening_hours[day].end} onChange={(e) => handleHourChange(day, 'end', e.target.value)} className="admin-input-neo-time" /></div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* CLOSURES TAB */}
                        {activeTab === 'closures' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Périodes de Fermeture</h3>
                                <div className="section-header-flex">
                                    <h4 className="sub-section-title">Calendrier des congés</h4>
                                    <button type="button" className="add-network-btn-mini">+ Ajouter une période</button>
                                </div>
                                <div className="empty-state-settings" style={{ textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', margin: '20px 0' }}>
                                    <span style={{ color: '#64748b' }}>Aucune période de fermeture prévue.</span>
                                </div>

                                <h4 className="sub-section-title">Messages & Alertes</h4>
                                <div className="closure-toggles">
                                    <div className="toggle-box-card">
                                        <div className="toggle-info">
                                            <strong>Bannière Publique</strong>
                                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>Afficher une alerte d'absence sur le site public.</p>
                                        </div>
                                        <label className="switch-neo"><input type="checkbox" name="closure_banner_active" checked={formData.closure_banner_active} onChange={handleChange} /><span className="slider-neo"></span></label>
                                    </div>
                                    <div className="form-group-stack">
                                        <label>Auto-Répondeur Client</label>
                                        <textarea name="closure_auto_responder" value={formData.closure_auto_responder} onChange={handleChange} className="admin-input-neo" rows="3"></textarea>
                                    </div>

                                    <div className="toggle-box-card danger-zone-mini" style={{ borderLeft: '4px solid #ef4444', marginTop: '40px' }}>
                                        <div className="toggle-info">
                                            <strong style={{ color: '#ef4444' }}>Mode "Indisponible" Immédiat</strong>
                                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>Désactiver temporairement les fonctions de chat.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className={`busy-btn ${formData.busy_mode_active ? 'active' : ''}`}
                                            onClick={() => setFormData(prev => ({ ...prev, busy_mode_active: !prev.busy_mode_active }))}
                                        >
                                            {formData.busy_mode_active ? "Désactiver l'Indisponibilité" : "Activer l'Indisponibilité"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'legal' && (
                            <div className="settings-tab-pane">
                                <h3 className="tab-title">Mon Profil</h3>
                                <div className="profile-edit-section">
                                    <div className="profile-mini-card" style={{ display: 'flex', gap: '20px', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '25px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <div className="profile-avatar-big" style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: '900', color: 'white' }}>AD</div>
                                        <div className="profile-details">
                                            <strong style={{ display: 'block', fontSize: '1.1rem', color: '#f1f5f9' }}>Administrateur Coup d'Projo</strong>
                                            <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{formData.email_public}</span>
                                        </div>
                                    </div>
                                    <div className="grid-2-settings" style={{ marginTop: '30px' }}>
                                        <div className="form-group-stack"><label>Prénom</label><input type="text" name="profile_firstName" value={formData.profile_firstName} onChange={handleChange} className="admin-input-neo" /></div>
                                        <div className="form-group-stack"><label>Nom</label><input type="text" name="profile_lastName" value={formData.profile_lastName} onChange={handleChange} className="admin-input-neo" /></div>
                                    </div>
                                </div>

                                <h3 className="tab-title" style={{ marginTop: '60px' }}>Mentions Légales</h3>
                                <div className="grid-2-settings">
                                    <div className="form-group-stack">
                                        <label>Directeur de Publication</label>
                                        <input type="text" value="Gérant Coup d'Projo" className="admin-input-neo" readOnly />
                                    </div>
                                    <div className="form-group-stack">
                                        <label>Hébergeur</label>
                                        <input type="text" value="Digital Ocean / Infocolab" className="admin-input-neo" readOnly />
                                    </div>
                                </div>
                                
                                <h4 className="sub-section-title">Documents contractuels</h4>
                                <div className="legal-docs-stack" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {['Mentions Légales', 'CGV / CGU', 'Politique de Confidentialité'].map(doc => (
                                        <div key={doc} className="doc-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <span style={{ fontSize: '0.95rem', color: '#cbd5e1' }}>{doc}</span>
                                            <button type="button" className="add-network-btn-mini">Modifier</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>

                    <div className="settings-footer-actions">
                        <button type="submit" className="complete-btn-neo" disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Settings;
